import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { beginSocialSignIn, type SocialProvider } from '@/api/auth/socialAuth';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/locale';

import { FormAlert } from './fields';

/*
 * Google / Apple identity providers (web/docs/data-layer.md §Auth). Both are
 * `outline` — quiet bordered secondaries — so the email/password submit stays
 * the one accent action on the view. `beginSocialSignIn` leaves the app via
 * location.assign, so the button only needs an in-flight state for the async
 * PKCE setup.
 *
 * On the 2-col layout the full "Continue with Google" label overflows, so the
 * buttons show the short brand name and carry the full phrase as `aria-label`.
 */

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path d="M21.6 12.23c0-.68-.06-1.36-.19-2.02H12v3.83h5.4a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.89-1.74 2.97-4.3 2.97-7.33Z" />
      <path d="M12 22c2.7 0 4.96-.9 6.62-2.42l-3.23-2.5c-.9.6-2.05.95-3.39.95-2.6 0-4.81-1.76-5.6-4.12H3.06v2.58A10 10 0 0 0 12 22Z" />
      <path d="M6.4 13.91a6 6 0 0 1 0-3.82V7.51H3.06a10 10 0 0 0 0 8.98L6.4 13.9Z" />
      <path d="M12 5.97c1.47 0 2.79.5 3.83 1.5l2.86-2.86A10 10 0 0 0 3.06 7.5l3.34 2.6c.79-2.36 3-4.13 5.6-4.13Z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.98-2.53 4.08ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z" />
    </svg>
  );
}

export function SocialButtons({ returnTo }: { returnTo: string }) {
  const [pending, setPending] = useState<SocialProvider | null>(null);
  const [failed, setFailed] = useState<SocialProvider | null>(null);
  const t = useT();

  async function begin(provider: SocialProvider) {
    setPending(provider);
    setFailed(null);
    try {
      await beginSocialSignIn(provider, returnTo);
    } catch {
      // The PKCE setup failed before leaving the page — say so, don't just reset.
      setPending(null);
      setFailed(provider);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          aria-label={t('login.continueWithGoogle')}
          disabled={pending !== null}
          onClick={() => void begin('Google')}
        >
          {pending === 'Google' ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <GoogleIcon />
          )}
          {t('auth.google')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          aria-label={t('login.continueWithApple')}
          disabled={pending !== null}
          onClick={() => void begin('SignInWithApple')}
        >
          {pending === 'SignInWithApple' ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <AppleIcon />
          )}
          {t('auth.apple')}
        </Button>
      </div>
      {failed ? (
        <FormAlert>
          {t('auth.socialError', { provider: failed === 'Google' ? t('auth.google') : t('auth.apple') })}
        </FormAlert>
      ) : null}
    </div>
  );
}
